// The realtime client every screen uses: one WebSocket per poll, reconnect with
// backoff, snapshots applied in order, and a vote queue that survives a dropped
// connection (BACKLOG #13: votes queued and retried on flaky wifi). No DOM types:
// the socket and the timers are injected, so it runs in tests and native shells.
import { ServerMessage, type ErrorCode, type Snapshot } from "./contracts";

export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export type ConnectionState = "connecting" | "open" | "reconnecting" | "gone";

export interface PendingVote {
  id: string;
  choices: number[];
  /** Strict-voting polls only. */
  fingerprint?: string;
  /** The question it answers (question sets); the room refuses it once the host has moved on. */
  q?: number;
}

export interface Timers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface ConnectionOptions {
  url: string;
  createSocket: (url: string) => SocketLike;
  voterToken?: string;
  onSnapshot: (snapshot: Snapshot) => void;
  onState?: (state: ConnectionState) => void;
  onAck?: (id: string, choices: number[]) => void;
  onVoteError?: (id: string | undefined, error: ErrorCode) => void;
  /** Called while the socket is down, so screens keep updating. */
  fallbackPoll?: () => Promise<Snapshot | null>;
  /**
   * Votes over HTTP. Used while the room says it is busy, and in a big room
   * where this client was turned away from the socket.
   */
  httpVote?: (vote: PendingVote) => Promise<{ ok: true; choices: number[] } | { ok: false; error: ErrorCode | "network" }>;
  /** A vote that was waiting when the page closed. */
  restoredPending?: PendingVote | null;
  onPendingChange?: (pending: PendingVote | null) => void;
  timers?: Timers;
  random?: () => number;
}

const OPEN = 1;
const PING_MS = 25_000;
const FALLBACK_POLL_MS = 3_000;
/** A big room's viewers read an edge-cached snapshot this often instead of holding a socket. */
const BIG_ROOM_POLL_MS = 1_500;
/** How long a turned-away client waits before trying the socket again. */
const BIG_ROOM_RETRY_MS = 60_000;
/** The close code a room uses to turn a viewer away from a full room. */
export const CLOSE_BIG_ROOM = 4429;
const HTTP_RETRY_MS = 2_000;
/**
 * A socket that sends no snapshot this long after connecting is treated as stalled. Corporate
 * proxies (Zscaler, 2026-10-08) complete the upgrade and then pass no frames and never close, so
 * without this the screen waited forever and the HTTP fallback never started.
 */
const FIRST_SNAP_MS = 5_000;
const BACKOFF_MAX_MS = 10_000;

export class PollConnection {
  private socket: SocketLike | null = null;
  private state: ConnectionState = "connecting";
  private lastSeq = -1;
  private attempts = 0;
  private pending: PendingVote | null;
  private reconnectTimer: unknown = null;
  private pingTimer: unknown = null;
  private pollTimer: unknown = null;
  private closed = false;
  private busy = false;
  private bigRoom = false;
  /** The last socket stalled: read and vote over HTTP until a socket delivers a snapshot again. */
  private stalled = false;
  private stallTimer: unknown = null;
  private httpInFlight = false;
  private httpRetryTimer: unknown = null;
  private nextId = 0;
  private readonly timers: Timers;
  private readonly random: () => number;

  constructor(private readonly o: ConnectionOptions) {
    this.timers = o.timers ?? (globalThis as unknown as Timers);
    this.random = o.random ?? Math.random;
    this.pending = o.restoredPending ?? null;
    this.connect();
  }

  /** Records a vote. Only the latest choice matters, so a newer vote replaces a queued one. */
  vote(choices: number[], fingerprint?: string, q?: number): string {
    const id = `v${Date.now().toString(36)}${(this.nextId++).toString(36)}`;
    this.setPending({ id, choices, ...(fingerprint ? { fingerprint } : {}), ...(q !== undefined ? { q } : {}) });
    this.flush();
    return id;
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  get pendingVote(): PendingVote | null {
    return this.pending;
  }

  close(): void {
    this.closed = true;
    this.clearTimers();
    if (this.socket) {
      this.socket.onclose = null;
      try {
        this.socket.close(1000, "bye");
      } catch {
        // already closed
      }
    }
    this.socket = null;
  }

  private connect(): void {
    if (this.closed) return;
    const s = this.o.createSocket(this.o.url);
    this.socket = s;
    if (this.stallTimer) this.timers.clearTimeout(this.stallTimer);
    this.stallTimer = this.timers.setTimeout(() => this.onStall(), FIRST_SNAP_MS);
    s.onopen = () => {
      this.attempts = 0;
      // After a stall, an open socket proves nothing; its first snapshot does (onMessage).
      if (!this.stalled) {
        this.bigRoom = false;
        this.setState("open");
        this.stopFallbackPoll();
      }
      this.schedulePing();
      this.flush();
    };
    s.onmessage = (ev) => this.onMessage(ev.data);
    s.onerror = () => {
      // onclose follows and handles it
    };
    s.onclose = (ev) => {
      this.socket = null;
      if (this.pingTimer) this.timers.clearTimeout(this.pingTimer);
      if (this.stallTimer) this.timers.clearTimeout(this.stallTimer);
      this.stallTimer = null;
      if (this.closed) return;
      if (ev.code === 4404) {
        this.setState("gone");
        this.closed = true;
        this.clearTimers();
        return;
      }
      if (ev.code === CLOSE_BIG_ROOM) {
        // Turned away from a full room: read the cached snapshot, vote over
        // HTTP, and look for a socket again much later. Nothing is wrong, so
        // the screen keeps saying "open".
        this.bigRoom = true;
        this.setState("open");
        this.startFallbackPoll(BIG_ROOM_POLL_MS);
        this.reconnectTimer = this.timers.setTimeout(() => this.connect(), BIG_ROOM_RETRY_MS);
        this.flush();
        return;
      }
      if (this.stalled) {
        // Still on HTTP after a stall; try the socket again later, quietly.
        this.reconnectTimer = this.timers.setTimeout(() => this.connect(), BIG_ROOM_RETRY_MS);
        return;
      }
      this.setState("reconnecting");
      this.startFallbackPoll();
      this.scheduleReconnect();
    };
  }

  /** No snapshot arrived on the socket in time: drop it and carry on over HTTP. */
  private onStall(): void {
    this.stallTimer = null;
    const s = this.socket;
    if (!s || this.closed) return;
    s.onopen = s.onmessage = s.onclose = s.onerror = null;
    try {
      s.close(1000, "stalled");
    } catch {
      // already closed
    }
    this.socket = null;
    if (this.pingTimer) this.timers.clearTimeout(this.pingTimer);
    this.stalled = true;
    this.bigRoom = true; // the same HTTP path a turned-away viewer uses
    this.setState("open");
    this.startFallbackPoll(FALLBACK_POLL_MS, true);
    this.reconnectTimer = this.timers.setTimeout(() => this.connect(), BIG_ROOM_RETRY_MS);
    this.flush();
  }

  private onMessage(data: unknown): void {
    let msg: ServerMessage;
    try {
      msg = ServerMessage.parse(JSON.parse(String(data)));
    } catch {
      return;
    }
    switch (msg.t) {
      case "snap":
        if (this.stallTimer) this.timers.clearTimeout(this.stallTimer);
        this.stallTimer = null;
        if (this.stalled) {
          // The socket works again: back to it.
          this.stalled = false;
          this.bigRoom = false;
          this.setState("open");
          this.stopFallbackPoll();
          this.flush();
        }
        this.applySnapshot(msg.snapshot);
        break;
      case "ack":
        if (this.pending?.id === msg.id) this.setPending(null);
        this.o.onAck?.(msg.id, msg.choices);
        break;
      case "err":
        // A refused vote is final (closed, invalid); retrying will not help.
        if (msg.id && this.pending?.id === msg.id && msg.error !== "rate_limited") this.setPending(null);
        this.o.onVoteError?.(msg.id, msg.error);
        break;
      case "pong":
        break;
    }
  }

  private applySnapshot(snapshot: Snapshot): void {
    if (snapshot.seq < this.lastSeq) return; // an older snapshot arriving late
    this.lastSeq = snapshot.seq;
    const wasBusy = this.busy;
    this.busy = snapshot.busy === true;
    this.o.onSnapshot(snapshot);
    if (this.busy && !wasBusy) this.flush();
  }

  /** Whether votes go over HTTP right now. */
  private get viaHttp(): boolean {
    return !!this.o.httpVote && (this.busy || this.bigRoom);
  }

  private flush(): void {
    if (!this.pending || !this.o.voterToken) return;
    if (this.viaHttp) return void this.flushHttp();
    const s = this.socket;
    if (!s || s.readyState !== OPEN) return;
    const p = this.pending;
    s.send(JSON.stringify({ t: "vote", voterToken: this.o.voterToken, choices: p.choices, id: p.id, ...(p.fingerprint ? { fingerprint: p.fingerprint } : {}), ...(p.q !== undefined ? { q: p.q } : {}) }));
  }

  private async flushHttp(): Promise<void> {
    const p = this.pending;
    if (!p || this.httpInFlight || this.closed) return;
    this.httpInFlight = true;
    let r: Awaited<ReturnType<NonNullable<ConnectionOptions["httpVote"]>>>;
    try {
      r = await this.o.httpVote!(p);
    } catch {
      r = { ok: false, error: "network" };
    }
    this.httpInFlight = false;
    if (this.closed) return;
    if (r.ok) {
      if (this.pending?.id === p.id) this.setPending(null);
      this.o.onAck?.(p.id, r.choices);
    } else if (r.error === "network" || r.error === "rate_limited") {
      // Keep the vote and try again; a newer vote replaces it meanwhile.
      if (!this.httpRetryTimer)
        this.httpRetryTimer = this.timers.setTimeout(() => {
          this.httpRetryTimer = null;
          this.flush();
        }, HTTP_RETRY_MS);
      return;
    } else {
      if (this.pending?.id === p.id) this.setPending(null);
      this.o.onVoteError?.(p.id, r.error);
    }
    // A newer vote may have arrived while this one was in flight.
    if (this.pending && this.pending.id !== p.id) this.flush();
  }

  private setPending(p: PendingVote | null): void {
    this.pending = p;
    this.o.onPendingChange?.(p);
  }

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.o.onState?.(state);
  }

  private scheduleReconnect(): void {
    const base = Math.min(BACKOFF_MAX_MS, 500 * 2 ** this.attempts);
    const delay = base / 2 + this.random() * (base / 2);
    this.attempts++;
    this.reconnectTimer = this.timers.setTimeout(() => this.connect(), delay);
  }

  private schedulePing(): void {
    this.pingTimer = this.timers.setTimeout(() => {
      if (this.socket?.readyState === OPEN) this.socket.send('{"t":"ping"}');
      this.schedulePing();
    }, PING_MS);
  }

  private startFallbackPoll(every = FALLBACK_POLL_MS, now = false): void {
    if (!this.o.fallbackPoll || this.pollTimer) return;
    const tick = async () => {
      this.pollTimer = this.timers.setTimeout(tick, every);
      try {
        const snap = await this.o.fallbackPoll!();
        if (snap && (this.state !== "open" || this.bigRoom)) this.applySnapshot(snap);
      } catch {
        // offline; the next tick tries again
      }
    };
    this.pollTimer = this.timers.setTimeout(tick, now ? 0 : every);
  }

  private stopFallbackPoll(): void {
    if (this.pollTimer) this.timers.clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }

  private clearTimers(): void {
    for (const t of [this.reconnectTimer, this.pingTimer, this.pollTimer, this.httpRetryTimer, this.stallTimer]) if (t) this.timers.clearTimeout(t);
    this.reconnectTimer = this.pingTimer = this.pollTimer = this.httpRetryTimer = this.stallTimer = null;
  }
}
